import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG } from '../constants/api.constants';

export interface BackendInteraction {
  id: number;
  type: string;
  action: string;
  description: string;
  timestamp: string;
  user: string;
  user_jobtitle?: string;
  customer_name?: string;
  customer_id?: number;
  company_id: number;
  company_name?: string;
  company_registration_number?: string;
  created_by: number;
  affected_entities?: any;
}

export interface InteractionsResponse {
  success: boolean;
  interactions: BackendInteraction[];
  count: number;
}

export interface ContractInfo {
  contract_id?: number;
  policynumber?: string;
  operation?: string;
  status?: string;
  startdate?: string;
  insurancepremium?: number;
  annualpremium?: number;
  currency?: string;
  productid?: number;
  product?: string;
  insurancecompanyid?: number;
  insurancecompany?: string;
  
  // Customer és Company ID-k és nevek
  policyholdercustomerid?: number;
  policyholdercustomer?: string;
  policyholdercompanyid?: number;
  policyholdercompany?: string;
  insuredcustomerid?: number;
  insuredcustomer?: string;
  insuredcompanyid?: number;
  insuredcompany?: string;
  enddate?: string;
  settlementdate?: string;
  paymentfrequency?: string;
  // Dinamikus mezők támogatása
  [key: string]: any;
}

export interface AdminInteraction {
  id: number;
  date: string;
  notes: string;
  status: string;
  created_by: string;
  user_jobtitle: string;
  customer_name?: string;
  company_name?: string;
  company_registration_number?: string;
  customer_id?: number;
  company_id?: number;
  affected_entities?: any; // Lehet object vagy array - biztosító műveleteknél object
  contract_info?: ContractInfo; // Szerződés információk szerződés műveleteknél
}

export interface LogsQueryParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string[];
  user?: string[];
  userId?: number;
  targetUserId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginatedLogsResponse {
  items: AdminInteraction[];
  total: number;
  page: number;
  page_size: number;
}

export interface StatusOption {
  value: string;
  label: string;
}

export interface Interaction {
  interactionid: number;
  customerid?: number;
  companyid: number;
  interactiondate: string;
  notes: string;
  status: string;
  createdby: number;
  user_fullname?: string;
  user_jobtitle?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InteractionsService {
  private baseUrl = `${API_CONFIG.BASE_URL}`;

  constructor(private http: HttpClient) {}

  /**
   * Céghez tartozó interactions lekérdezése
   */
  getCompanyInteractions(companyId: number): Observable<Interaction[]> {
    return this.http.get<InteractionsResponse>(`${this.baseUrl}/interactions/company/${companyId}`)
      .pipe(
        map(response => {
          // Convert backend format to frontend format
          return response.interactions.map(item => ({
            interactionid: item.id,
            customerid: item.customer_id,
            companyid: item.company_id,
            interactiondate: item.timestamp,
            notes: item.description,
            status: item.action,
            createdby: item.created_by,
            user_fullname: item.user,
            user_jobtitle: item.user_jobtitle || 'Call Center munkatárs'
          }));
        })
      );
  }

  /**
   * Új interaction létrehozása
   */
  createInteraction(interaction: Partial<Interaction>): Observable<Interaction> {
    return this.http.post<Interaction>(`${this.baseUrl}/interactions/`, interaction);
  }

  /**
   * Összes interaction lekérdezése admin panel számára (szerver oldali paginálás + szűrés)
   */
  getAllInteractions(params: LogsQueryParams): Observable<PaginatedLogsResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('page_size', params.pageSize.toString());
    
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach(s => httpParams = httpParams.append('status', s));
    }
    if (params.user && params.user.length > 0) {
      params.user.forEach(u => httpParams = httpParams.append('user', u));
    }
    if (params.dateFrom) {
      httpParams = httpParams.set('date_from', params.dateFrom);
    }
    if (params.dateTo) {
      httpParams = httpParams.set('date_to', params.dateTo);
    }
    if (params.userId !== undefined && params.userId !== null) {
      httpParams = httpParams.set('user_id', params.userId.toString());
    }
    if (params.targetUserId !== undefined && params.targetUserId !== null) {
      httpParams = httpParams.set('target_user_id', params.targetUserId.toString());
    }
    
    return this.http.get<PaginatedLogsResponse>(`${this.baseUrl}/interactions/all`, { params: httpParams });
  }

  /**
   * Státusz opciók lekérdezése a szűrő dropdown-hoz
   * Az aktuális szűrőknek megfelelően csak azok a státuszok jönnek vissza,
   * amelyek a szűrt halmazban előfordulnak.
   */
  getStatusOptions(params?: Omit<LogsQueryParams, 'page' | 'pageSize' | 'status'>): Observable<StatusOption[]> {
    let httpParams = new HttpParams();
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params?.user && params.user.length > 0) {
      params.user.forEach(u => httpParams = httpParams.append('user', u));
    }
    if (params?.dateFrom) {
      httpParams = httpParams.set('date_from', params.dateFrom);
    }
    if (params?.dateTo) {
      httpParams = httpParams.set('date_to', params.dateTo);
    }
    return this.http.get<StatusOption[]>(`${this.baseUrl}/interactions/status-options`, { params: httpParams });
  }

  /**
   * Céghez tartozó interactions lekérdezése admin formátumban
   * (a extended-logs-table komponens számára)
   */
  getCompanyInteractionsForLogs(companyId: number): Observable<AdminInteraction[]> {
    return this.http.get<InteractionsResponse>(`${this.baseUrl}/interactions/company/${companyId}`)
      .pipe(
        map(response => {
          // Convert backend format to AdminInteraction format
          return response.interactions.map(item => {
            // Szerződés információk kinyerése az affected_entities-ből (ugyanúgy mint az /all endpoint-ban)
            let contract_info: ContractInfo | undefined = undefined;
            if (item.affected_entities && item.affected_entities[0] && "contract_id" in item.affected_entities[0]) {
              // Minden nem-null értéket hozzáadunk
              contract_info = {} as ContractInfo;
              for (const key in item.affected_entities[0]) {
                const value = item.affected_entities[0][key];
                if (value !== null && value !== "") {
                  (contract_info as any)[key] = value;
                }
              }
            }

            return {
              id: item.id,
              date: item.timestamp,
              notes: item.description,
              status: item.action,
              created_by: item.user,
              user_jobtitle: item.user_jobtitle || 'Call Center munkatárs',
              customer_name: item.customer_name,
              company_name: item.company_name,
              company_registration_number: item.company_registration_number,
              customer_id: item.customer_id,
              company_id: item.company_id,
              affected_entities: item.affected_entities,
              contract_info: contract_info
            };
          });
        })
      );
  }

  /**
   * Ügyfélhez tartozó interactions lekérdezése admin formátumban
   * (a extended-logs-table komponens számára)
   */
  getCustomerInteractionsForLogs(customerId: number): Observable<AdminInteraction[]> {
    return this.http.get<InteractionsResponse>(`${this.baseUrl}/interactions/customer/${customerId}`)
      .pipe(
        map(response => {
          // Convert backend format to AdminInteraction format
          return response.interactions.map(item => {
            // Szerződés információk kinyerése az affected_entities-ből (ugyanúgy mint az /all endpoint-ban)
            let contract_info: ContractInfo | undefined = undefined;
            if (item.affected_entities && item.affected_entities[0] && "contract_id" in item.affected_entities[0]) {
              // Minden nem-null értéket hozzáadunk
              contract_info = {} as ContractInfo;
              for (const key in item.affected_entities[0]) {
                const value = item.affected_entities[0][key];
                if (value !== null && value !== "") {
                  (contract_info as any)[key] = value;
                }
              }
            }

            return {
              id: item.id,
              date: item.timestamp,
              notes: item.description,
              status: item.action,
              created_by: item.user,
              user_jobtitle: item.user_jobtitle || 'Call Center munkatárs',
              customer_name: item.customer_name,
              company_name: item.company_name,
              company_registration_number: item.company_registration_number,
              customer_id: item.customer_id,
              company_id: item.company_id,
              affected_entities: item.affected_entities,
              contract_info: contract_info
            };
          });
        })
      );
  }

  /**
   * Felhasználó legutolsó aktivitásának lekérdezése
   */
  getUserLastActivity(userId: number): Observable<{success: boolean, timestamp: string | null}> {
    return this.http.get<{success: boolean, timestamp: string | null}>(`${this.baseUrl}/interactions/user/${userId}/last-activity`);
  }

  /**
   * Felhasználói interakció létrehozása (pl. beállítás változtatás)
   */
  createUserInteraction(interactionData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/interactions/`, interactionData);
  }

  /**
   * Mai napi tevékenységek számának lekérdezése
   */
  getTodayInteractionsCount(): Observable<{success: boolean, count: number}> {
    return this.http.get<{success: boolean, count: number}>(`${this.baseUrl}/interactions/today/count`);
  }
}
